import * as React from 'react';
import { RouteComponentProps, Redirect } from 'react-router-dom';
import { SiteInfo, SiteInfoProp, useSiteInfo, useAddSiteInfo, useEditSiteInfo } from '../../hooks/useSiteInfo';
import { SiteImage, useSiteImages } from '../../hooks/useSiteImages';
import SiteInfoModal from './SiteInfoModal';
import AdminSiteCharacteristics from './AdminSiteCharacteristics';
import * as i18next from 'i18next';
import { withTranslation } from 'react-i18next';
import DefaultPromptModal from '../Controls/DefaultPromptModal';
import moment from 'moment';
import Spinner from '../Controls/Spinner';
import Chip from '@mui/material/Chip';
import { isRecentlyUpdated } from '../../constants/recentlyUpdated';
import TextArea from 'react-textarea-autosize';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';

function convertToSiteInfoProps(siteInfo: Partial<SiteInfo>): SiteInfoProp[] {
    return [
        { column: 'CustomerNumber', value: siteInfo.customerNumber || '' },
        { column: 'SiteName', value: siteInfo.siteName || '' },
        { column: 'Brand', value: siteInfo.brand || '' },
        { column: 'StreetAddress', value: siteInfo.streetAddress || '' },
        { column: 'PostalCode', value: siteInfo.postalCode || '' },
        { column: 'Town', value: siteInfo.town || '' },
        { column: 'TelephoneExchange', value: siteInfo.telephoneExchange || '' },
        { column: 'TelephoneAlternative', value: siteInfo.telephoneAlternative || '' },
        { column: 'Contact', value: siteInfo.contact || '' },
        { column: 'OpeningHours', value: siteInfo.openingHours || '' },
        { column: 'Published', value: siteInfo.published ? 'true' : 'false' }
    ];
}

function SiteInfoForm({ match, history }: RouteComponentProps<{ siteId: string; addMode: string }>) {
    const uniqueSiteId = parseInt(match.params.siteId);
    const isAddMode = match.params.addMode === 'true';

    const [sitename, setSitename] = React.useState('');
    const [siteInfoProps, setSiteInfoProps] = React.useState<SiteInfoProp[]>(convertToSiteInfoProps({}));
    const [newImages, setNewImages] = React.useState<SiteImage[]>([]);
    const [existingSiteImages, setExistingSiteImages] = React.useState<SiteImage[]>([]);
    const [showDeleteSiteModal, setShowDeleteSiteModal] = React.useState(false);
    const [goBack, setGoBack] = React.useState(false);
    const [showNotSupportedFileTypeModal, setShowNotSupportedFileTypeModal] = React.useState(false);
    const [showDoubleImagesModal, setShowDoubleImagesModal] = React.useState(false);
    const [showSiteUpdatedModal, setShowSiteUpdatedModal] = React.useState(false);
    const [showSiteCreatedModal, setShowSiteCreatedModal] = React.useState(false);
    const [lastUpdatedBy, setLastUpdatedBy] = React.useState('');
    const [updateDateUTC, setUpdateDateUTC] = React.useState<Date | null>(null);
    const [newSiteId, setNewSiteId] = React.useState<number | null>(null);

    const { data: siteInfoData, isLoading } = useSiteInfo(isAddMode ? 0 : uniqueSiteId);
    const { data: siteImagesData } = useSiteImages(isAddMode ? 0 : uniqueSiteId);
    const addSiteInfoMutation = useAddSiteInfo();
    const editSiteInfoMutation = useEditSiteInfo();

    React.useEffect(() => {
        if (siteInfoData) {
            setSitename(siteInfoData.siteName || '');
            setSiteInfoProps(convertToSiteInfoProps(siteInfoData));
            setLastUpdatedBy(siteInfoData.lastUpdatedBy || '');
            setUpdateDateUTC(siteInfoData.updateDateUTC);
        }
    }, [siteInfoData]);

    React.useEffect(() => {
        if (siteImagesData != null) setExistingSiteImages(siteImagesData);
    }, [siteImagesData]);

    React.useEffect(() => {
        if (editSiteInfoMutation.isSuccess) setShowSiteUpdatedModal(true);
    }, [editSiteInfoMutation.isSuccess]);

    if (goBack) return <Redirect to={'/administrationsites'} />;
    if (isLoading) return <Spinner />;

    function updateState(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const id = e.target.id;
        const val = id === 'Published' ? (e.target as HTMLInputElement).checked.toString() : e.target.value;
        setSiteInfoProps(prev => prev.map(si => si.column === id ? { ...si, value: val } : si));
    }

    function handleNewImage(file: File, e: React.ChangeEvent<HTMLInputElement>) {
        if (file.type.search('image') === -1) {
            e.target.value = '';
            setShowNotSupportedFileTypeModal(true);
            return;
        }
        const alreadyExists = newImages.find(x => x.filename === file.name)
            || existingSiteImages.find(x => decodeURI(decodeURI(x.filename)) === file.name);
        if (alreadyExists) {
            e.target.value = '';
            setShowDoubleImagesModal(true);
            return;
        }
        const siteImage: SiteImage = { uniqueSiteId, siteImageId: 0, imageUrl: '', file, base64: '', filename: file.name };
        const reader = new FileReader();
        reader.onload = () => {
            siteImage.base64 = reader.result ? reader.result.toString() : '';
            setNewImages(prev => [...prev, siteImage]);
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    }

    function createFormData(infos: SiteInfoProp[], imgs: SiteImage[], existingImgs: SiteImage[]) {
        const formData = new FormData();
        infos.forEach(si => formData.append(si.column, si.value));
        imgs.forEach(img => formData.append('files', img.file));
        existingImgs.forEach(img => formData.append('existingImages', img.imageUrl));
        return formData;
    }

    function addSiteInfo(e: React.MouseEvent) {
        e.preventDefault();
        addSiteInfoMutation.mutate(createFormData(siteInfoProps, newImages, existingSiteImages), {
            onSuccess: (id) => { setNewSiteId(id); setShowSiteCreatedModal(true); }
        });
    }

    function updateSiteInfo(e: React.MouseEvent) {
        e.preventDefault();
        editSiteInfoMutation.mutate({ siteId: uniqueSiteId, data: createFormData(siteInfoProps, newImages, existingSiteImages) });
    }

    function renderSiteInfo(si: SiteInfoProp) {
        if (si.column === 'Published') {
            return (
                <Grid key={si.column} container alignItems="center" spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography component="label" htmlFor={si.column}>{i18next.t('site.' + si.column)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    id={si.column}
                                    checked={si.value === 'true'}
                                    onChange={updateState}
                                    color="primary"
                                />
                            }
                            label=""
                        />
                    </Grid>
                </Grid>
            );
        }
        return (
            <Grid key={si.column} container alignItems="flex-start" spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography component="label" htmlFor={si.column} sx={{ pt: 1, display: 'block' }}>
                        {i18next.t('site.' + si.column)}
                    </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 8 }}>
                    <TextArea
                        className="form-control"
                        style={{ resize: 'none', width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: 4, fontFamily: 'inherit', fontSize: '1rem' }}
                        minRows={1}
                        id={si.column}
                        value={si.value}
                        onChange={updateState}
                    />
                </Grid>
            </Grid>
        );
    }

    function renderSiteImage(image: SiteImage, src: string, onRemove: () => void) {
        return (
            <Grid key={image.filename} size={{ xs: 6, sm: 4, md: 3 }}>
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 1, position: 'relative' }}>
                    <img style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }} src={src} alt={image.filename} />
                    <IconButton
                        size="small"
                        color="error"
                        onClick={onRemove}
                        sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'white', '&:hover': { bgcolor: 'grey.100' } }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {decodeURI(decodeURI(image.filename))}
                    </Typography>
                </Paper>
            </Grid>
        );
    }

    return (
        <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 8 }}>
                <Box className="site" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h5" fontWeight={600}>
                        {isAddMode ? i18next.t('adminsites.addsite') : `${i18next.t('adminsites.changesite')} ${sitename}`}
                    </Typography>
                    {!isAddMode && isRecentlyUpdated(updateDateUTC) && (
                        <Chip size="small" label={i18next.t('site.recentlyUpdated')} data-testid="recently-updated" color="warning"
                            sx={{ height: 18, fontWeight: 600, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }} />
                    )}
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {siteInfoProps.map(si => renderSiteInfo(si))}

                    <Divider sx={{ mt: 1 }} />
                    <Typography variant="subtitle1" fontWeight={600}>{i18next.t('adminsites.images')}</Typography>
                    <Divider />

                    {!isAddMode && existingSiteImages.length > 0 && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>{i18next.t('adminsites.existingimages')}</Typography>
                            <Grid container spacing={2}>
                                {existingSiteImages.map(img => renderSiteImage(img, img.imageUrl,
                                    () => setExistingSiteImages(prev => prev.filter(x => x.imageUrl !== img.imageUrl))))}
                            </Grid>
                        </Box>
                    )}

                    <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>{i18next.t('adminsites.newimages')}</Typography>
                        {newImages.length > 0 && (
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                {newImages.map(img => renderSiteImage(img, img.base64,
                                    () => setNewImages(prev => prev.filter(x => x.base64 !== img.base64))))}
                            </Grid>
                        )}
                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={<AddPhotoAlternateIcon />}
                            htmlFor="file"
                        >
                            {i18next.t('adminsites.chooseimage')}
                            <input
                                id="file"
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={(e) => e.target.files && handleNewImage(e.target.files[0], e)}
                            />
                        </Button>
                    </Box>

                    <Divider />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        {!isAddMode && (
                            <Button
                                variant="contained"
                                color="error"
                                onClick={(e) => { e.preventDefault(); setShowDeleteSiteModal(true); }}
                            >
                                {i18next.t('adminsites.deletebutton')}
                            </Button>
                        )}
                        <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto' }}>
                            <Button variant="contained" color="primary" onClick={isAddMode ? addSiteInfo : updateSiteInfo}>
                                {isAddMode ? i18next.t('adminsites.savebutton') : i18next.t('adminsites.updatebutton')}
                            </Button>
                            <Button variant="outlined" color="inherit" onClick={() => setGoBack(true)}>
                                {i18next.t('adminsites.cancelbutton')}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Grid>

            {!isAddMode && (
                <Grid size={{ xs: 12, md: 4 }}>
                    <AdminSiteCharacteristics uniqueSiteId={uniqueSiteId} />
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box>
                            <Typography variant="body2" color="text.secondary">{i18next.t('common.lastupdated')}</Typography>
                            <Typography variant="body2">{updateDateUTC ? moment.utc(updateDateUTC).local().format('L HH:mm') : ''}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">{i18next.t('common.updatedby')}</Typography>
                            <Typography variant="body2">{lastUpdatedBy}</Typography>
                        </Box>
                    </Box>
                </Grid>
            )}

            <SiteInfoModal siteId={uniqueSiteId} sitename={sitename} show={showDeleteSiteModal}
                handleClose={(deleted) => { if (deleted) setGoBack(true); else setShowDeleteSiteModal(false); }} />
            <DefaultPromptModal show={showNotSupportedFileTypeModal} handleClose={() => setShowNotSupportedFileTypeModal(false)}
                closeText={i18next.t('common.close')} header={i18next.t('adminsiteimage.notsupported.header')} body={i18next.t('adminsiteimage.notsupported.body')} />
            <DefaultPromptModal show={showDoubleImagesModal} handleClose={() => setShowDoubleImagesModal(false)}
                closeText={i18next.t('common.close')} header={i18next.t('adminsiteimage.doubleimages.header')} body={i18next.t('adminsiteimage.doubleimages.body')} />
            <DefaultPromptModal show={showSiteUpdatedModal} handleClose={() => setShowSiteUpdatedModal(false)}
                closeText={i18next.t('common.close')} header={i18next.t('adminsites.updatedheader')} body={i18next.t('adminsites.updatedbody')} />
            <DefaultPromptModal show={showSiteCreatedModal} handleClose={() => { setShowSiteCreatedModal(false); if (newSiteId) history.push('/administrationsiteinfo/' + newSiteId); }}
                closeText={i18next.t('common.close')} header={i18next.t('adminsites.createdheader')} body={i18next.t('adminsites.createdbody')} />
        </Grid>
    );
}

export default withTranslation('translations')(SiteInfoForm);
